/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { describeError } from '@/lib/api';

/**
 * useAsyncAction — wrapper per azioni asincrone dei pulsanti:
 *  • `busy` true durante l'esecuzione → disabilita il pulsante e mostra lo spinner
 *  • ignora i click multipli mentre è in corso (anti doppio invio)
 *  • intercetta gli errori e mostra un toast leggibile (rete assente, 4xx/5xx) senza rompere la UI
 *
 *   const { run, busy } = useAsyncAction();
 *   <Button disabled={busy} onClick={() => run(() => api.post(...), { success: 'Fatto!' })}>
 */
export const useAsyncAction = () => {
  const [busy, setBusy] = useState(false);
  const running = useRef(false);

  const run = useCallback(async (fn, { success, error, silent = false, onError } = {}) => {
    if (running.current) return undefined; // click multiplo: ignorato
    running.current = true;
    setBusy(true);
    try {
      const res = await fn();
      if (success) toast.success(typeof success === 'function' ? success(res) : success);
      return res;
    } catch (e) {
      if (!silent) toast.error(error || describeError(e));
      onError?.(e);
      return undefined;
    } finally {
      running.current = false;
      setBusy(false);
    }
  }, []);

  return { run, busy };
};

export default useAsyncAction;
